import { put, takeLatest } from 'redux-saga/effects';

import analytics from '@xto10x/core/lib/analytics';
import auth from '@xto10x/core/lib/cognito.auth';
import storage from '@xto10x/storage/lib';
import feedbackApi from 'services/feedbackApi';
import integrationApi from 'services/integrationApi';
import userApi from 'services/userApi';

import notificationApi from 'services/notificationApi';
import shortid from 'shortid';
import { AnalyticsCategory } from 'utils/analytics.constants';
import { StorageKey } from 'utils/constants';
import {
  actions,
  getIntegrations,
  getNotificationPreferenceDetail,
  saveUserImportedReviews,
  saveUserPastReviews,
  saveReviewModuleSettings
} from './actions';

import {
  fetchUserModules,
  hideAppLoading,
  saveIntegrations,
  saveNotificationPreferences,
  showAlertMessage,
  showAppLoading
} from '../../App/store/actions';

function* updateUserNameWorker({ name, id }) {
  try {
    yield put(showAppLoading());
    const { data } = yield userApi.updateUser({
      user_reference_id: id,
      name
    });
    yield put(fetchUserModules(data.user_reference_id));
    yield put(showAlertMessage('success', 'Name updated'));
    yield put(hideAppLoading());
    analytics.trackEvent(AnalyticsCategory.account, 'update_name');
  } catch (error) {
    analytics.trackError('update_user_name_error', error);
    yield put(hideAppLoading());
    yield put(showAlertMessage('error', error.message));
  }
}

export function* updateUserNameWatcher() {
  yield takeLatest(actions.UPDATE_USER_NAME, updateUserNameWorker);
}

function* updateProfilePictureWorker({ imagePath }) {
  try {
    yield put(showAppLoading());
    const authenticatedUser = yield auth
      .currentAuthenticatedUser(true)
      .catch(() => null);
    if (
      !storage.getDataFromLocal(StorageKey.REQUESTER_USER_ID) &&
      !authenticatedUser
    ) {
      yield put(
        showAlertMessage(
          'error',
          'Ooops! Something went wrong, please try again.'
        )
      );
      analytics.trackError(
        'update_profile_picture_worker',
        new Error('user id is not available')
      );
      return;
    }
    const image = yield storage
      .saveFileInS3(
        storage.getDataFromLocal(StorageKey.REQUESTER_USER_ID) ||
          authenticatedUser.username,
        imagePath
      )
      .then(img => `${img}?iuk=${shortid.generate()}`);
    yield userApi.updateUser({
      user_reference_id: storage.getDataFromLocal(StorageKey.REQUESTER_USER_ID),
      image
    });
    yield put(showAlertMessage('success', 'Image updated'));
    yield put(hideAppLoading());
    analytics.trackEvent(AnalyticsCategory.account, 'update_photo');
  } catch (error) {
    analytics.trackError('update_profile_picture_error', error);
    yield put(hideAppLoading());
    yield put(showAlertMessage('error', error.message));
  }
}

export function* updateProfilePictureWatcher() {
  yield takeLatest(actions.UPDATE_PROFILE_PICTURE, updateProfilePictureWorker);
}

function* removeProfilePictureWorker() {
  try {
    yield put(showAppLoading());
    const authenticatedUser = yield auth
      .currentAuthenticatedUser(true)
      .catch(() => null);
    if (
      !storage.getDataFromLocal(StorageKey.REQUESTER_USER_ID) &&
      !authenticatedUser
    ) {
      yield put(
        showAlertMessage(
          'error',
          'Ooops! Something went wrong, please try again.'
        )
      );
      analytics.trackError(
        'remove_profile_picture_worker',
        new Error('user id is not available')
      );
      return;
    }
    yield storage.removeFileFromS3(
      storage.getDataFromLocal(StorageKey.REQUESTER_USER_ID) ||
        authenticatedUser.username
    );
    yield userApi.updateUser({
      user_reference_id: storage.getDataFromLocal(StorageKey.REQUESTER_USER_ID),
      image: ''
    });
    yield put(showAlertMessage('success', 'Image removed'));
    yield put(hideAppLoading());
    analytics.trackEvent(AnalyticsCategory.account, 'remove_photo');
  } catch (error) {
    analytics.trackError('remove_profile_picture_error', error);
    yield put(hideAppLoading());
    yield put(showAlertMessage('error', error.message));
  }
}

export function* removeProfilePictureWatcher() {
  yield takeLatest(actions.REMOVE_PROFILE_PICTURE, removeProfilePictureWorker);
}

function* getNotificationPreferenceWorker() {
  try {
    yield put(showAppLoading());
    const { data } = yield notificationApi.getNotificationPreference();
    yield put(saveNotificationPreferences(data));
    yield put(hideAppLoading());
  } catch (error) {
    analytics.trackError('get_notification_pref_error', error);
    yield put(hideAppLoading());
    yield put(showAlertMessage('error', error.message));
  }
}

export function* getNotificationPreferenceWatcher() {
  yield takeLatest(
    actions.GET_NOTIFICATION_PREFERENCE,
    getNotificationPreferenceWorker
  );
}

function* updateNotificationPreferenceWorker({
  requestBody,
  notificationPreferences
}) {
  try {
    yield put(showAppLoading());
    const { data } = yield notificationApi.updateNotificationPreference(
      requestBody
    );
    if (notificationPreferences?.length) {
      let notificationPreferencesCopy = [...notificationPreferences];
      notificationPreferencesCopy = notificationPreferencesCopy.map(
        preference => {
          data.forEach(updatedPreference => {
            if (
              preference.notification_type === 'REMINDER' &&
              preference.channel === updatedPreference.channel &&
              preference.notification_type ===
                updatedPreference.notification_type
            )
              // eslint-disable-next-line no-param-reassign
              preference = updatedPreference;
          });
          return preference;
        }
      );
      yield put(saveNotificationPreferences(notificationPreferencesCopy));
    } else yield put(getNotificationPreferenceDetail());
    yield put(hideAppLoading());
  } catch (error) {
    analytics.trackError('update_notification_pref_error', error);
    yield put(hideAppLoading());
    yield put(showAlertMessage('error', error.message));
  }
}

export function* updateNotificationPreferenceWatcher() {
  yield takeLatest(
    actions.UPDATE_NOTIFICATION_PREFERENCE,
    updateNotificationPreferenceWorker
  );
}

function* toggleNotificationPreferenceWorker({
  requestBody,
  notificationPreferences
}) {
  try {
    yield put(showAppLoading());
    const { data } = yield notificationApi.toggleNotificationPreference(
      requestBody
    );
    if (notificationPreferences?.length) {
      let notificationPreferencesCopy = [...notificationPreferences];
      let notificationDetailsPresent = false;
      notificationPreferencesCopy = notificationPreferencesCopy.map(
        preference => {
          if (
            preference.channel === data.channel &&
            preference.notification_type === data.notification_type
          ) {
            notificationDetailsPresent = true;
            return data;
          }
          return preference;
        }
      );
      if (!notificationDetailsPresent)
        yield put(getNotificationPreferenceDetail());
      else yield put(saveNotificationPreferences(notificationPreferencesCopy));
    } else yield put(getNotificationPreferenceDetail());
    yield put(hideAppLoading());
  } catch (error) {
    analytics.trackError('toggle_notification_pref_error', error);
    try {
      yield put(getNotificationPreferenceDetail());
    } catch (e) {
      // Do nothing
    }
    yield put(hideAppLoading());
    yield put(showAlertMessage('error', error.message));
  }
}

function* updateChannelDndSettingsWorker({
  requestBody,
  notificationPreferences
}) {
  try {
    yield put(showAppLoading());
    const { data } = yield notificationApi.updateChannelDndSettings(
      requestBody
    );
    if (notificationPreferences?.length) {
      let notificationPreferencesCopy = [...notificationPreferences];
      let isSlackDndDetailsPresent = false;
      notificationPreferencesCopy = notificationPreferencesCopy.map(
        preference => {
          if (
            preference.channel === 'SLACK' &&
            preference.notification_type !== 'REMINDER'
          ) {
            isSlackDndDetailsPresent = true;
            // eslint-disable-next-line no-param-reassign
            preference.dnd = data.dnd;
          }
          return preference;
        }
      );
      if (!isSlackDndDetailsPresent)
        yield put(getNotificationPreferenceDetail());
      else yield put(saveNotificationPreferences(notificationPreferencesCopy));
    } else yield put(getNotificationPreferenceDetail());
    yield put(hideAppLoading());
  } catch (error) {
    analytics.trackError('update_channel_dnd_setting_error', error);
    yield put(hideAppLoading());
    yield put(showAlertMessage('error', error.message));
  }
}

export function* toggleNotificationPreferenceWatcher() {
  yield takeLatest(
    actions.TOGGLE_NOTIFICATION_PREFERENCE,
    toggleNotificationPreferenceWorker
  );
}

export function* updateChannelDndSettingsWatcher() {
  yield takeLatest(
    actions.UPDATE_CHANNEL_DND_SETTING,
    updateChannelDndSettingsWorker
  );
}

function* getIntegrationsWorker() {
  try {
    yield put(showAppLoading());
    const integrations = yield integrationApi
      .getIntegrations()
      .then(res => res.data);
    yield put(saveIntegrations(integrations));
    yield put(hideAppLoading());
  } catch (error) {
    analytics.trackError('get_integrations', error);
    yield put(hideAppLoading());
    yield put(showAlertMessage('error', error.message));
  }
}

export function* getIntegrationsWatcher() {
  yield takeLatest(actions.GET_INTEGRATIONS_ACTION, getIntegrationsWorker);
}

function* updateIntegrationsWorker({ bodyResponse }) {
  try {
    yield put(showAppLoading());
    yield integrationApi.updateIntegration(bodyResponse);
    yield put(getIntegrations());
    yield put(hideAppLoading());
  } catch (error) {
    analytics.trackError('update_integrations', error);
    yield put(hideAppLoading());
    yield put(showAlertMessage('error', error.message));
  }
}

export function* updateIntegrationsWatcher() {
  yield takeLatest(
    actions.UPDATE_INTEGRATIONS_ACTION,
    updateIntegrationsWorker
  );
}

function* fetchUserPastReviewsWorker({ userId }) {
  try {
    yield put(showAppLoading());
    const {
      data: { result }
    } = yield feedbackApi.getUserPastReviews(userId);
    yield put(saveUserPastReviews(result));
  } catch (error) {
    yield put(showAlertMessage('error', error.message));
  } finally {
    yield put(hideAppLoading());
  }
}

function* fetchReviewModuleSettingsWorker() {
  try {
    yield put(showAppLoading());
    const {
      data: {
        result: {
          module: { reviews }
        }
      }
    } = yield feedbackApi.getReviewsModuleSettings();
    yield put(saveReviewModuleSettings(reviews));
  } catch (error) {
    yield put(showAlertMessage('error', error.message));
  } finally {
    yield put(hideAppLoading());
  }
}

function* fetchUserImportedReviewsWorker({ userId }) {
  try {
    yield put(showAppLoading());
    const {
      data: { result }
    } = yield feedbackApi.getUserImportedReviews(userId);
    yield put(saveUserImportedReviews(result));
  } catch (error) {
    yield put(showAlertMessage('error', error.message));
  } finally {
    yield put(hideAppLoading());
  }
}

export function* fetchUserPastReviewsWatcher() {
  yield takeLatest(actions.FETCH_USER_PAST_REVIEWS, fetchUserPastReviewsWorker);
}

export function* fetchReviewModuleSettingsWatcher() {
  yield takeLatest(
    actions.FETCH_REVIEW_MODULE_ORG_SETTINGS,
    fetchReviewModuleSettingsWorker
  );
}

export function* fetchUserImportedReviewsWatcher() {
  yield takeLatest(
    actions.FETCH_USER_IMPORTED_REVIEWS,
    fetchUserImportedReviewsWorker
  );
}
